/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import { useId, useState } from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';
import { useCartItems } from '../../../../util/cart';

import {
  AvatarLarge,
  ExternalLink,
  IconArrowHead,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const intl = useIntl();
  const submenuId = useId();
  const [expanded, setExpanded] = useState(false);
  const { text, type, href, route, sections, showSectionsOnMobile } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  const renderLink = link =>
    link.type === 'internal' && link.route ? (
      <NamedLink name={link.route.name} params={link.route.params} to={link.route.to}>
        <span className={css.menuItemBorder} />
        {link.text}
      </NamedLink>
    ) : (
      <ExternalLink href={link.href}>
        <span className={css.menuItemBorder} />
        {link.text}
      </ExternalLink>
    );

  // Only links that opt in expose their dropdown children in the mobile menu.
  const subSections = showSectionsOnMobile && sections?.length ? sections : [];
  const hasSubLinks = subSections.some(section => section.items?.length);

  const parentClassName =
    type === 'internal' && route
      ? classNames(css.navigationLink, getCurrentPageClass(route.name))
      : css.navigationLink;

  return (
    <>
      <li className={classNames(parentClassName, { [css.expandableNavigation]: hasSubLinks })}>
        {renderLink({ text, type, href, route })}
        {hasSubLinks ? (
          <button
            type="button"
            className={css.submenuToggle}
            aria-expanded={expanded}
            aria-controls={submenuId}
            aria-label={intl.formatMessage(
              { id: 'TopbarMobileMenu.toggleSubmenu' },
              { label: text }
            )}
            onClick={() => setExpanded(open => !open)}
          >
            <IconArrowHead
              direction="down"
              size="small"
              rootClassName={classNames(css.submenuChevron, {
                [css.submenuChevronExpanded]: expanded,
              })}
            />
          </button>
        ) : null}
      </li>
      {hasSubLinks ? (
        <li id={submenuId} className={css.submenuLinks} hidden={!expanded}>
          {subSections.map((section, sectionIndex) => (
            <React.Fragment key={`${text}_${section.label || sectionIndex}`}>
              {section.label ? <span className={css.submenuLabel}>{section.label}</span> : null}
              {(section.items || []).map(subLink =>
                subLink.disabled ? (
                  <span key={subLink.text} className={css.submenuDisabled}>
                    {subLink.text}
                  </span>
                ) : subLink.type === 'internal' && subLink.route ? (
                  <NamedLink
                    key={subLink.text}
                    name={subLink.route.name}
                    params={subLink.route.params}
                    to={subLink.route.to}
                    className={classNames(css.submenuLink, getCurrentPageClass(subLink.route.name))}
                  >
                    {subLink.text}
                  </NamedLink>
                ) : (
                  <ExternalLink key={subLink.text} href={subLink.href} className={css.submenuLink}>
                    {subLink.text}
                  </ExternalLink>
                )
              )}
            </React.Fragment>
          ))}
        </li>
      ) : null}
    </>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
    showMyShopLink,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const cartItems = useCartItems();

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage
          id="TopbarMobileMenu.signupOrLogin"
          values={{ lineBreak: <br />, signup, login }}
        />
      </span>
    );
    return (
      <nav className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <ul className={css.customLinksWrapper}>{extraLinks}</ul>

          <div className={css.spacer} />
        </div>
        <div className={css.footer}>{createListingsLinkMaybe}</div>
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const manageListingsLinkMaybe =
    showMyShopLink && user.id?.uuid ? (
      <li className={classNames(css.navigationLink, currentPageClass('ShopPage'))}>
        <NamedLink name="ShopPage" params={{ id: user.id.uuid }}>
          My Shop
        </NamedLink>
      </li>
    ) : null;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <ul className={css.customLinksWrapper}>{extraLinks}</ul>
        <ul className={css.accountLinksWrapper}>
          <li className={css.navigationLink}>
            <NamedLink name="CartPage" ariaLabel="Cart" title="Cart">
              <span className={css.cartIcon} aria-hidden="true">
                🛒
              </span>
              {cartItems.length ? <span className={css.cartCount}>{cartItems.length}</span> : null}
            </NamedLink>
          </li>
          {manageListingsLinkMaybe}
          <li className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}>
            <NamedLink name="ProfileSettingsPage">
              <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
            </NamedLink>
          </li>
          <li className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}>
            <NamedLink name="AccountSettingsPage">
              <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
            </NamedLink>
          </li>
        </ul>
        <ul className={css.accountLinksWrapper}>
          <li className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}>
            <NamedLink name="InboxPage" params={{ tab: inboxTab }}>
              <FormattedMessage id="TopbarMobileMenu.inboxLink" />
              {notificationCountBadge}
            </NamedLink>
          </li>
        </ul>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>{createListingsLinkMaybe}</div>
    </div>
  );
};

export default TopbarMobileMenu;
